import { Logger } from '@foundation-player/logger';
import { EncryptionType } from '@foundation-player/runtime';
import type { AudioTrack, PlayerBitrate, TextTrack, VideoTrack } from '@foundation-player/types';
import { PlayerEvent } from '../event-emitter/events';
import { EventDispatcher, FoundationEventBase } from '../event-emitter/unfiltered';
import { Media } from '../media';
import { PlayerContainer } from '../player-container';
import { RequestHeader, RequestHeaderCallback, RequestType } from '../request/request-header';

interface PlayerAdapterState {
  time?: number;
  finished: boolean;
  loaded: boolean;
  stalled: boolean;
  source: string;
  duration?: number;
}

/*
 * Player adapters generally listen to their respective root player events
 * (BitmovinAdapter to BitmovinEvents, etc)
 */
export abstract class AbstractPlayerAdapter<Events, EventType> {
  protected currentState: PlayerAdapterState;
  protected drmType: EncryptionType = EncryptionType.None;
  protected media?: Media;
  protected emitEvent: EventDispatcher;

  protected requestHeaderCallback?: RequestHeaderCallback;
  protected maxBitrate: PlayerBitrate = {
    video: 0,
    audio: 0,
  };
  /**
   * list of handler unsubscriptions to remove event handlers in destroy
   *
   * @see {@link AbstractPlayerAdapter.unsubscribeAll}
   * @see {@link AbstractPlayerAdapter.addUnsubscriber}
   */
  protected unsubscribers: (() => void)[] = [];

  protected constructor(
    protected playerContainer: PlayerContainer,
    protected loggerPlayerAdapter: Logger,
  ) {
    this.currentState = this.getInitialState();
    this.emitEvent = (event: FoundationEventBase) => {
      playerContainer.dispatchEvent(event);
    };

    playerContainer.addEventListener(PlayerEvent.StallStarted, () => {
      this.currentState.stalled = true;
    });

    playerContainer.addEventListener(PlayerEvent.StallEnded, () => {
      this.currentState.stalled = false;
    });
  }

  protected addEventListener(name: Events, handler: (event: EventType) => void): () => void {
    this.on(name, handler);
    const unsubscriber = () => {
      this.off(name, handler);
    };
    this.unsubscribers.push(unsubscriber);
    return unsubscriber;
  }

  protected removeEventListener(name: Events, handler: (event: EventType) => void): void {
    this.off(name, handler);
  }

  protected abstract on(name: Events, handler: (event: EventType) => void): void;
  protected abstract off(name: Events, handler: (event: EventType) => void): void;

  /**
   * adds unsubscription handler for unsubscription, so they can be easily removed
   *
   * @see {@link AbstractPlayerAdapter.unsubscribeAll}
   * @see {@link AbstractPlayerAdapter.unsubscribers}
   */
  protected addUnsubscriber(unsubscriber: () => void) {
    this.unsubscribers.push(unsubscriber);
  }

  /**
   * unsubscribes all registered handlers, should be called in destroy
   *
   * @see {@link AbstractPlayerAdapter.addUnsubscriber}
   * @see {@link AbstractPlayerAdapter.unsubscribers}
   */
  protected unsubscribeAll() {
    while (this.unsubscribers.length) {
      const unsubscribe = this.unsubscribers.pop();
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (err) {
          this.loggerPlayerAdapter.error('error during unsubscribe:', err);
        }
      }
    }
  }

  isFullscreen() {
    return this.playerContainer.isFullscreen();
  }

  async getRequestHeader<T = RequestType>(type: RequestType & T): Promise<RequestHeader | undefined> {
    if (!this.requestHeaderCallback) {
      return;
    }

    // Warning: Bitmovin will silently fail if some error occurs while processing the drm callback or we return a Promise.reject()
    try {
      const result = await this.requestHeaderCallback(type);
      this.loggerPlayerAdapter.debug(`Result of requestHeaderCallback for type '${type}':`, result);
      return result;
    } catch (error) {
      this.loggerPlayerAdapter.error(`Error while processing requestHeaderCallback for type '${type}':`, error);
    }
  }

  setRequestHeaderCallback(callback: RequestHeaderCallback) {
    this.requestHeaderCallback = callback;
  }

  abstract getVideoTracks(): VideoTrack[];

  getAvailableVideoTracks(): VideoTrack[] {
    return this.getVideoTracks();
  }

  getActiveVideoTrack(): VideoTrack | undefined {
    return this.getVideoTracks().find((track) => track.isActive);
  }

  abstract getTextTracks(): TextTrack[];

  getAvailableTextTracks(): TextTrack[] {
    return this.getTextTracks().filter(this.isEmptyLanguageTrack.bind(this));
  }

  getActiveTextTrack(): TextTrack | undefined {
    return this.getTextTracks().find((track) => track.isActive);
  }

  abstract getAudioTracks(): AudioTrack[];

  getAvailableAudioTracks(): AudioTrack[] {
    return this.getAudioTracks().filter(this.isEmptyLanguageTrack.bind(this));
  }

  getActiveAudioTrack(): AudioTrack | undefined {
    return this.getAudioTracks().find((track) => track.isActive);
  }

  protected isEmptyLanguageTrack(track: TextTrack) {
    if (!track.language) {
      this.loggerPlayerAdapter.warn(`Track with id '${track.id}' was filtered out because it has no set language`);
    }
    return !!track.language;
  }

  setMaxBitrate(bitrate: Partial<PlayerBitrate>) {
    this.maxBitrate = {
      ...this.maxBitrate,
      ...bitrate,
    };
  }

  getMaxBitrate() {
    return this.maxBitrate;
  }

  getCachedDuration() {
    return this.currentState.duration || 0;
  }

  protected fireMediaLoadedEventOnce(): void {
    if (this.currentState.loaded) {
      return;
    }

    this.currentState.loaded = true;
    this.emitEvent(new FoundationEventBase(PlayerEvent.MediaLoaded));
  }

  protected getInitialState(): PlayerAdapterState {
    return {
      time: undefined,
      duration: undefined,
      finished: false,
      loaded: false,
      stalled: false,
      source: '',
    };
  }

  protected resetCurrentState() {
    this.currentState = this.getInitialState();
  }

  abstract getVideoElement(): HTMLVideoElement | undefined;

  show(): Promise<void> {
    const videoElement = this.getVideoElement();
    if (videoElement) videoElement.style.display = 'block';

    return Promise.resolve();
  }

  hide(): Promise<void> {
    const videoElement = this.getVideoElement();
    if (videoElement) videoElement.style.display = 'none';

    return Promise.resolve();
  }

  canReUseVideoElement() {
    return false;
  }

  getPlayerContainerElement() {
    return this.playerContainer.getPlayerElement();
  }

  setDRMType(drmType: EncryptionType, emitEvent = true) {
    this.drmType = drmType;

    if (emitEvent) {
      this.emitEvent(new FoundationEventBase(PlayerEvent.KeySystemSelected));
    }
  }

  getDRMType(): EncryptionType {
    return this.drmType;
  }
}
